import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { BookOpen, Users, FolderKanban, History, Settings as SettingsIcon, Feather } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "@/lib/api";

const NAV = [
    { to: "/", label: "Dashboard", icon: Feather, testId: "nav-dashboard", end: true },
    { to: "/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
    { to: "/editors", label: "Editors", icon: Users, testId: "nav-editors" },
    { to: "/history", label: "History", icon: History, testId: "nav-history" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

// Offset from the Dashboard hero quote so the sidebar shows a *different* one on the same day.
const SIDEBAR_QUOTE_OFFSET = 3;
const SIDEBAR_FALLBACK = { text: "Words are animals alive with a will of their own.", author: "Carl Jung" };

export default function Layout() {
    const loc = useLocation();
    const [quotes, setQuotes] = useState([]);

    useEffect(() => {
        // Sidebar is persistent across routes; fetch once on mount.
        api
            .get("/settings")
            .then((r) => setQuotes(Array.isArray(r.data.quotes) ? r.data.quotes : []))
            .catch(() => setQuotes([]));
    }, []);

    const sidebarQuote = useMemo(() => {
        if (!quotes.length) return SIDEBAR_FALLBACK;
        const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
        return quotes[(day + SIDEBAR_QUOTE_OFFSET) % quotes.length];
    }, [quotes]);

    return (
        <div className="grain min-h-screen relative">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 min-h-screen">
                {/* Sidebar */}
                <aside className="lg:col-span-2 border-b lg:border-b-0 lg:border-r border-border bg-background/60 backdrop-blur-sm">
                    <div className="p-6 lg:p-8 sticky top-0">
                        <div className="flex items-center gap-2 mb-10">
                            <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                            <div>
                                <div className="font-serif text-lg leading-none">Slush</div>
                                <div className="label-mono mt-1">Submission Helper</div>
                            </div>
                        </div>
                        <nav className="flex lg:flex-col gap-1" data-testid="side-nav">
                            {NAV.map(({ to, label, icon: Icon, testId, end }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    end={end}
                                    data-testid={testId}
                                    className={({ isActive }) =>
                                        [
                                            "group flex items-center gap-3 px-3 py-2 rounded-sm text-sm relative",
                                            "transition-colors duration-200",
                                            isActive
                                                ? "text-foreground bg-secondary"
                                                : "text-foreground/70 hover:text-foreground hover:bg-secondary/60",
                                        ].join(" ")
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            <span
                                                className={
                                                    "absolute left-0 top-1 bottom-1 w-[2px] " +
                                                    (isActive ? "bg-[#8C2D19]" : "bg-transparent")
                                                }
                                            />
                                            <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                                            <span className="font-sans">{label}</span>
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>
                        <div className="hidden lg:block mt-16">
                            <div className="label-mono mb-2">Note</div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                A quiet room for matching manuscripts to the right editors, and drafting the letter that gets them read.
                            </p>
                        </div>

                        {/* Ambient quote strip — sourced from user's Settings > Dashboard quotes */}
                        <div
                            className="hidden lg:block mt-8 pt-6 border-t border-border"
                            data-testid="sidebar-quote"
                        >
                            <div className="label-mono mb-2">Quote of the day</div>
                            <p className="font-serif italic text-sm leading-relaxed text-foreground/80">
                                &ldquo;{sidebarQuote.text}&rdquo;
                            </p>
                            {sidebarQuote.author ? (
                                <p className="label-mono mt-2">— {sidebarQuote.author}</p>
                            ) : null}
                        </div>
                    </div>
                </aside>

                {/* Main */}
                <main className="lg:col-span-10 min-w-0" data-testid="main-content" key={loc.pathname}>
                    <div className="p-6 sm:p-10 lg:p-14 max-w-[1400px] fade-up">
                        <Outlet />
                    </div>
                </main>
            </div>
            <Toaster
                position="top-right"
                duration={7000}
                closeButton
                toastOptions={{
                    // Give errors plenty of time to be read; leave successes at default.
                    duration: 7000,
                    style: {
                        background: "#1B362F",
                        color: "#F4F1EB",
                        border: "1px solid #12241F",
                        borderRadius: "2px",
                        fontFamily: "Manrope, sans-serif",
                        maxWidth: "440px",
                    },
                    classNames: {
                        error: "toast-error-wide",
                    },
                }}
            />
        </div>
    );
}
